import "./GroupCardShared.scss";

import clsx from "clsx";
import type { FC, ReactNode } from "react";

import { userActivityIcons } from "components/materials/section/helpers";
import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { ListItem } from "mds/components/ListItem";
import { Tooltip } from "mds/components/Tooltip";
import { CircleInfoIcon, PersonSingleIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  pageGroupSelectors,
  selectActivityStatusForUser,
  selectSortedPageGroupUsersByPageGroupId,
  selectUserBySectionUserId,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { PROFILE_PROPERTIES, getUserFullName } from "utils/user-utils";

interface PageGroupInfoCardUserProps {
  sectionUserId: string;
}

const PageGroupInfoCardUser: FC<PageGroupInfoCardUserProps> = ({ sectionUserId }) => {
  const user = useObjectSelector(
    (s) => selectUserBySectionUserId(s, sectionUserId),
    PROFILE_PROPERTIES,
  );
  const activityStatus = useAppSelector((s) => selectActivityStatusForUser(s, user?.id));

  return (
    <ListItem className="gap-1">
      {userActivityIcons[activityStatus]}{" "}
      <div className="min-w-12 truncate">{getUserFullName(user)}</div>
    </ListItem>
  );
};

interface PageGroupInfoCardProps {
  className?: string;
  pageGroupId?: string | null;
  pageGroupIndex: number;
  children?: ReactNode;
  isActive: boolean;
  shouldFilterForReadiedUsers?: boolean;
  isInstructorView?: boolean;
}

export const PageGroupInfoCard: FC<PageGroupInfoCardProps> = ({
  className,
  pageGroupId,
  pageGroupIndex,
  children,
  shouldFilterForReadiedUsers = false,
  isActive,
  isInstructorView = false,
}) => {
  const pageGroup = useObjectSelector(
    (s) => pageGroupSelectors.selectById(s, pageGroupId),
    ["auto_created"],
  );
  const pageGroupUsers = useListSelector(
    (s) => selectSortedPageGroupUsersByPageGroupId(s, pageGroupId),
    ["id", "section_user_id", "is_ready"],
  );

  if (!pageGroup) return null;

  const filteredPageGroupUsers = shouldFilterForReadiedUsers
    ? pageGroupUsers.filter((pageGroupUser) => pageGroupUser.is_ready)
    : pageGroupUsers;

  return (
    <Card className={clsx("group-card", className)} isActive={isActive}>
      <div className="flex w-full items-center justify-between">
        <div className="h3 ml-2 flex items-center gap-1">
          {t("common.group_title", {
            number: pageGroupIndex + 1,
          })}
          {pageGroup.auto_created && (
            <Tooltip
              content={t(
                isInstructorView
                  ? "tooltip.auto_created_group"
                  : "tooltip.auto_created_group_student",
              )}
            >
              <CircleInfoIcon className="text-orange" size="s" />
            </Tooltip>
          )}
        </div>
        <div className="flex">
          <div className="h6 flex items-center justify-center gap-1 px-2 text-black-tint-40">
            <PersonSingleIcon />
            {filteredPageGroupUsers.length}
          </div>
          {children}
        </div>
      </div>
      <div className="flex flex-wrap">
        {filteredPageGroupUsers.map((pageGroupUser) => (
          <PageGroupInfoCardUser
            key={pageGroupUser.id}
            sectionUserId={pageGroupUser.section_user_id}
          />
        ))}
        {filteredPageGroupUsers.length === 0 && (
          <div className="body-s ml-2 pt-2 text-black-tint-55">
            {t("page_group_info_card.no_one_in_group")}
          </div>
        )}
      </div>
    </Card>
  );
};
